import React, { useState } from 'react'
import PropTypes from 'prop-types'

import { Label, Input } from 'components/Forms'
import PhoneInput from 'sharedComponents/Input/Phone'
import style from './style.module.scss'
import { Button } from 'components/Forms'
import Loader from 'components/Loader'
import { validatePhoneNumber } from 'services/validate-phone-number'
import Error from 'sharedComponents/Error'
import isEmail from 'validator/lib/isEmail'
import { set as setNotification } from 'store/notification/actions'
import { useDispatch } from 'react-redux'
import api from 'services/pet-notify-api'
import useUser from 'UserApp/hooks/useUser'

PetParentForm.propTypes = {
  values: PropTypes.object,
  onChange: PropTypes.func,
  petId: PropTypes.number,
  updatePet: PropTypes.func,
  verifyEmailOtp: PropTypes.func,
  verifyPhoneOtp: PropTypes.func,
  resendEmailOtp: PropTypes.func,
  resendPhoneOtp: PropTypes.func,
}

function PetParentForm(props) {
  const { onChange, petId, values, removePetParent, addPetParent, onClose, resendEmailOtp, resendPhoneOtp } = props

  const [isLoading, setIsLoading] = useState(false)
  const [isReassignPetParent, setIsReassignPetParent] = useState(true)
  const [currentView, setCurrentView] = useState('list')
  const [error, setError] = useState(null);
  const [reassignEmail, setReassignEmail] = useState('')
  const [reassignEmailUser, setReassignEmailUser] = useState(null)
  const { user } = useUser()
  const dispatch = useDispatch()

  const handleAddPetParent = () => {
    if (!values.petParent) {
      setError('Pet parent is required')
      return
    }
    if (!values.petParent.firstName) {
      setError('First name is required')
      return
    }
    if (!values.petParent.lastName) {
      setError('Last name is required')
      return
    }
    if (!values.petParent.email) {
      setError('Email is required')
      return
    }
    if (!isEmail(values.petParent.email)) {
      setError('Email must be a valid email address')
      return
    }
    if (!values.petParent.phone) {
      setError('Phone number is required')
      return
    }
    if (values && values.petParent && values.petParent.phone) {
      const validation = validatePhoneNumber(values.petParent.phone)

      if (!validation.valid) {
        setError(validation.message)
        return
      } else {
        setError(null)
      }
    }

    addPetParent(petId, {
      firstName: values.petParent.firstName,
      lastName: values.petParent.lastName,
      email: values.petParent.email,
      phone: values.petParent.phone,
    })
    dispatch(setNotification(`Pet parent added, We have sent you a verification email and SMS`))
    setCurrentView('list')
  }

  const handleRemovePetParent = async () => {
    setIsLoading(true)
    try {
      await removePetParent(petId)
      setIsReassignPetParent(false)
      if (onClose) {
        onClose()
      }
    } catch (error) {
      console.error('Error removing pet parent:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendVerification = (type) => {
    if (type === 'email') {
      resendEmailOtp(petId).then(() => {
        dispatch(setNotification('Verification email sent successfully'))
      }).catch((error) => {
        dispatch(setNotification('Failed to send verification email'))
      })
    } else if (type === 'phone') {
      resendPhoneOtp(petId).then(() => {
        dispatch(setNotification('Verification SMS sent successfully'))
      }).catch((error) => {
        dispatch(setNotification('Failed to send verification SMS'))
      })
    }
  }

  const handleReassignPetParent = async () => {
    if (!reassignEmailUser) {
      setError('User not found')
      return
    }
    try {
      setIsLoading(true)
      const response = await api.pets.reassignPet(petId, reassignEmailUser.id)
      setIsLoading(false)
      dispatch(setNotification('Pet parent re-assigned successfully'))
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error reassigning pet parent:', error)
      setError(error.message ?? error)
    }
  }

  const validateReassignEmail = async (email) => {
    if (!email) {
      setError('Email is required')
      return
    }
    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValidEmail = emailRegex.test(email);
      if (!isValidEmail) {
        setError('Email must be a valid email address')
        return
      }
      const response = await api.pets.validateEmail(email, user)
      setReassignEmailUser(response.user)
      setError(null)
    } catch (error) {
      console.error('Error validating reassign email:', error)
      setError(error.message ?? error)
      return
    }
  }

  return (
    <React.Fragment>

      {currentView === 'reassign-pet-parent' ?
        <p variant='note' className={style.description}>
          Re-assign a pet and its Intellitag ID account to another Pet Notify premium user. The previous parent will no longer receive notifications for this pet.
        </p>
        : <p variant='note' className={style.description}>
          Pet parents must reside at the same location for geo-fencing and emergency alarm
          notifications to work.
        </p>}

      {
        currentView === 'list' && (
          <>
            <div className={style.addPetParent}>
              {values.owner && (
                <>
                  <p>{values.owner.name}</p>
                  <p>{values.owner.email}</p>
                  <p>{values.owner.phone ? values.owner.phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') : ''}</p>
                  <p></p>
                </>
              )}
              {values.petParent && (
                <>
                  <p>{values.petParent.firstName} {values.petParent.lastName}</p>
                  <p>{values.petParent.email}</p>
                  <p>{values.petParent.phone ? values.petParent.phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') : ''}</p>
                  <p className={style.actions} onClick={() => handleRemovePetParent()}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14H6L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </p>
                </>
              )}

            </div>
            {
              (values.petParent && (!values.petParent.emailVerified || !values.petParent.phoneVerified)) && (
                <>
                  {
                    !values.petParent.emailVerified && (
                      <p className={style.verification} onClick={() => handleResendVerification('email')}>
                        Resend verification email
                      </p>
                    )
                  }
                  {
                    !values.petParent.phoneVerified && (
                      <p className={style.verification} onClick={() => handleResendVerification('phone')}>
                        Resend verification SMS
                      </p>
                    )
                  }
                </>
              )}
            <div className={style.buttons}>
              {!values.petParent && <Button onClick={() => setCurrentView('add')}>
                + Add Pet Parent</Button>}
              <Button onClick={() => setCurrentView('reassign-pet-parent')}>
                Reassign Pet Parent</Button>
            </div>
          </>
        )
      }

      {
        currentView === 'add' && (
          <>
            <Label htmlFor='pet-parent-name'>
              First Name <span style={{ fontWeight: 'lighter' }}>*</span>
            </Label>
            <Input
              name='petParent.firstName'
              value={values?.petParent?.firstName}
              onChange={onChange}
              id='pet-parent-first-name'
              required
              disabled={!isReassignPetParent}
            />

            <Label htmlFor='pet-parent-name'>
              Last Name <span style={{ fontWeight: 'lighter' }}>*</span>
            </Label>
            <Input
              name='petParent.lastName'
              value={values?.petParent?.lastName}
              onChange={onChange}
              id='pet-parent-last-name'
              required
              disabled={!isReassignPetParent}
            />

            <Label htmlFor='pet-parent-email'>
              Email <span style={{ fontWeight: 'lighter' }}>*</span>
            </Label>
            <Input
              name='petParent.email'
              value={values?.petParent?.email}
              onChange={onChange}
              id='pet-parent-email'
              type='email'
              required
              disabled={!isReassignPetParent}
            />

            <div className={style.phoneNumber}>
              <Label htmlFor='pet-parent-phone'>
                Phone Number <span style={{ fontWeight: 'lighter' }}>*</span>
              </Label>
              <PhoneInput
                name='petParent.phone'
                value={values?.petParent?.phone}
                onChange={onChange}
                id='pet-parent-phone'
                required
                disabled={!isReassignPetParent}
              />
            </div>

            {
              error && (
                <Error>{error}</Error>
              )
            }
            {isReassignPetParent &&
              <div className={style.buttons}>
                <Button type='button' small className={style.button} onClick={handleAddPetParent}>
                  Save
                </Button>
                {isLoading && <Loader />}
              </div>}
          </>
        )
      }

      {
        currentView === 'reassign-pet-parent' && (
          <>
            <Label htmlFor='reassign-email'>
              Email <span style={{ fontWeight: 'lighter' }}>*</span>
            </Label>
            <Input
              name='reassignEmail'
              value={reassignEmail}
              onChange={(e) => {
                setReassignEmailUser(null)
                setError(null)
                setReassignEmail(e.value)
                validateReassignEmail(e.value)
              }}
              id='reassign-email'
              type='email'
              required
            />
            {reassignEmailUser && (
              <div className={style.reassignPetParent}>
                <p>Name: {reassignEmailUser.firstName} {reassignEmailUser.lastName}</p>
                <p>Email: {reassignEmailUser.email}</p>
                <p>Phone: {reassignEmailUser.phone ? reassignEmailUser.phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') : ''}</p>
              </div>
            )}
            {
              error && (
                <Error>{error}</Error>
              )
            }
            <div className={style.buttons}>
              <Button type='button' small className={style.button} onClick={() => setCurrentView('reassign-pet-parent-confirm')} disabled={!reassignEmailUser}>
                Reassign Pet Parent
              </Button>
              {isLoading && <Loader />}
            </div>
          </>
        )
      }

      {
        currentView === 'reassign-pet-parent-confirm' && (
          <React.Fragment>
            <div className={style.reassignPetParentConfirm}>
              <h2>Re-Assign Pet?</h2>
              <p>In transferring your pet, you agree there will be no credit for prepaid Intellitag monitoring fees if evident.</p>
              <p>This pet and its Intellitag ID will transfer to the new Pet Notify user. The current parent will no longer receive alerts. </p>              
              <div className={style.buttons}>
                <Button type='button' small className={style.button} disabled={isLoading} onClick={() => setCurrentView('reassign-pet-parent')}>
                  Cancel
                </Button>
                <Button type='button' small className={style.button} disabled={isLoading} onClick={handleReassignPetParent}>
                  Yes, Re-Assign
                </Button>
              </div>
            </div>
          </React.Fragment>
        )
      }

    </React.Fragment>
  )
}

export default PetParentForm
